/**
 * Radial maze generator using PolarGrid algorithm with DFS carving.
 *
 * Reference: greentec/mazes-for-programmers-js PolarGrid
 *
 * Produces concentric rings where outer rings subdivide cells
 * when the arc length exceeds a threshold.
 */

import type {
  CanvasSize,
  RadialCell,
  RadialMazeState,
} from '../types/maze-types';
import type { GameDifficulty } from '@/contexts/games/types';

const TWO_PI = 2 * Math.PI;

/** Minimum starting sectors for ring 1 */
const BASE_SECTORS = 8;

/** Arc-length threshold ratio — when a cell's arc exceeds this multiple of ringWidth, subdivide */
const SUBDIVISION_THRESHOLD = 1.8;

/** Difficulty → ring count */
const RING_COUNT: Record<GameDifficulty, number> = {
  easy: 4,
  medium: 7,
  hard: 11,
  expert: 16,
};

/**
 * Build the sector layout for each ring, subdividing when arc length is too wide.
 */
function buildSectorLayout(rings: number, ringWidth: number): number[] {
  const sectorsPerRing: number[] = [1]; // ring 0 = centre (1 sector)

  if (rings > 1) {
    sectorsPerRing.push(BASE_SECTORS);
  }

  for (let r = 2; r < rings; r++) {
    const innerSectors = sectorsPerRing[r - 1];
    const radius = r * ringWidth;
    const arcLength = (TWO_PI * radius) / innerSectors;

    if (arcLength > SUBDIVISION_THRESHOLD * ringWidth) {
      // Double the sector count
      sectorsPerRing.push(innerSectors * 2);
    } else {
      sectorsPerRing.push(innerSectors);
    }
  }

  return sectorsPerRing;
}

/**
 * Create the cell grid with all walls up.
 */
function createCells(
  rings: number,
  sectorsPerRing: number[],
): RadialCell[][] {
  const cells: RadialCell[][] = [];

  for (let r = 0; r < rings; r++) {
    const ring: RadialCell[] = [];
    for (let s = 0; s < sectorsPerRing[r]; s++) {
      ring.push({
        ring: r,
        sector: s,
        walls: {
          inward: r > 0,    // centre has no inward wall
          outward: true,
          cw: true,
          ccw: true,
        },
        visited: false,
      });
    }
    cells.push(ring);
  }

  return cells;
}

/** Neighbor descriptor for DFS carving */
interface Neighbor {
  ring: number;
  sector: number;
  direction: 'inward' | 'outward' | 'cw' | 'ccw';
}

/**
 * Get unvisited neighbors of a cell in the polar grid.
 */
function getUnvisitedNeighbors(
  cell: RadialCell,
  cells: RadialCell[][],
  sectorsPerRing: number[],
): Neighbor[] {
  const neighbors: Neighbor[] = [];
  const { ring: r, sector: s } = cell;
  const sectorCount = sectorsPerRing[r];

  // Inward (toward centre)
  if (r > 0) {
    const innerSectors = sectorsPerRing[r - 1];
    const ratio = sectorCount / innerSectors;
    const innerSector = Math.floor(s / ratio);
    if (!cells[r - 1][innerSector].visited) {
      neighbors.push({ ring: r - 1, sector: innerSector, direction: 'inward' });
    }
  }

  // Outward (toward edge) — may map to multiple children, pick one unvisited
  if (r < cells.length - 1) {
    const outerSectors = sectorsPerRing[r + 1];
    const ratio = outerSectors / sectorCount;
    const startSector = Math.floor(s * ratio);
    const endSector = Math.floor((s + 1) * ratio);
    for (let os = startSector; os < endSector; os++) {
      if (!cells[r + 1][os].visited) {
        neighbors.push({ ring: r + 1, sector: os, direction: 'outward' });
      }
    }
  }

  // Clockwise
  const cwSector = (s + 1) % sectorCount;
  if (!cells[r][cwSector].visited) {
    neighbors.push({ ring: r, sector: cwSector, direction: 'cw' });
  }

  // Counter-clockwise
  const ccwSector = (s - 1 + sectorCount) % sectorCount;
  if (!cells[r][ccwSector].visited) {
    neighbors.push({ ring: r, sector: ccwSector, direction: 'ccw' });
  }

  return neighbors;
}

/**
 * Remove the wall between the current cell and the neighbor.
 */
function removeWall(
  cells: RadialCell[][],
  current: { ring: number; sector: number },
  neighbor: Neighbor,
): void {
  const curCell = cells[current.ring][current.sector];
  const nbCell = cells[neighbor.ring][neighbor.sector];

  switch (neighbor.direction) {
    case 'inward':
      curCell.walls.inward = false;
      nbCell.walls.outward = false;
      break;
    case 'outward':
      curCell.walls.outward = false;
      nbCell.walls.inward = false;
      break;
    case 'cw':
      curCell.walls.cw = false;
      nbCell.walls.ccw = false;
      break;
    case 'ccw':
      curCell.walls.ccw = false;
      nbCell.walls.cw = false;
      break;
  }
}

/**
 * Carve the maze using iterative DFS (randomized depth-first search).
 * Starts from the centre cell to ensure solvability.
 */
function carveMaze(
  cells: RadialCell[][],
  sectorsPerRing: number[],
): void {
  const stack: Array<{ ring: number; sector: number }> = [];

  // Start from centre
  cells[0][0].visited = true;
  stack.push({ ring: 0, sector: 0 });

  while (stack.length > 0) {
    const current = stack[stack.length - 1];
    const cell = cells[current.ring][current.sector];
    const neighbors = getUnvisitedNeighbors(cell, cells, sectorsPerRing);

    if (neighbors.length === 0) {
      stack.pop();
      continue;
    }

    // Pick a random unvisited neighbor
    const chosen = neighbors[Math.floor(Math.random() * neighbors.length)];
    removeWall(cells, current, chosen);

    cells[chosen.ring][chosen.sector].visited = true;
    stack.push({ ring: chosen.ring, sector: chosen.sector });
  }
}

/**
 * Generate a radial maze.
 *
 * @param canvasSize - Canvas dimensions in pixels
 * @param difficulty - Game difficulty (maps to ring count)
 * @returns RadialMazeState with concentric rings, entrance on outermost ring, exit at centre
 */
export function generateRadialMaze(
  canvasSize: CanvasSize,
  difficulty: GameDifficulty,
): RadialMazeState {
  const rings = RING_COUNT[difficulty];
  const maxRadius = Math.min(canvasSize.width, canvasSize.height) / 2;
  const padding = 20;
  const ringWidth = (maxRadius - padding) / rings;

  const sectorsPerRing = buildSectorLayout(rings, ringWidth);
  const cells = createCells(rings, sectorsPerRing);

  carveMaze(cells, sectorsPerRing);

  // Entrance: random sector on outermost ring with outward wall removed
  const outerRing = rings - 1;
  const entranceSector = Math.floor(
    Math.random() * sectorsPerRing[outerRing],
  );
  cells[outerRing][entranceSector].walls.outward = false;

  // Exit: centre cell (ring 0, sector 0)
  const exit = { ring: 0, sector: 0 };

  return {
    rings,
    sectorsPerRing,
    cells,
    center: {
      x: canvasSize.width / 2,
      y: canvasSize.height / 2,
    },
    ringWidth,
    canvasSize,
    entrance: { ring: outerRing, sector: entranceSector },
    exit,
    pathSegments: [],
    isCompleted: false,
  };
}
